import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock, Trash2, PackageOpen, Loader2, Plus, MessageCircle, Pencil } from "lucide-react";
import ProductEditDialog from "@/components/ProductEditDialog";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, fileUrl, getAdminToken, formatBRL, formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import FavoriteButton from "@/components/FavoriteButton";
import AdminPanel from "@/components/AdminPanel";
import Reveal from "@/components/Reveal";

const WHATS_NUMBER = "5522999989203";

const galleryOf = (p) => ((p.images?.length ? p.images : [p.storage_path]).filter(Boolean));

const previewText = (s) => (s || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

export const Catalog = () => {
    const qc = useQueryClient();
    const { add } = useCart();
    const [adminOpen, setAdminOpen] = useState(false);
    const [isAdmin, setIsAdmin] = useState(!!getAdminToken());
    const [selected, setSelected] = useState(null);
    const [imgIndex, setImgIndex] = useState(0);
    const [editProduct, setEditProduct] = useState(null);
    useEffect(() => setImgIndex(0), [selected]);
    const { data, isLoading } = useQuery({
        queryKey: ["products"],
        queryFn: async () => (await api.get("/products")).data,
    });
    const products = data?.products ?? [];

    const onDelete = async (id) => {
        try {
            await api.delete(`/products/${id}`, {
                headers: { Authorization: `Bearer ${getAdminToken()}` },
            });
            qc.invalidateQueries({ queryKey: ["products"] });
            toast.success("Peça removida do catálogo.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    return (
        <section className="pb-24 pt-28 md:pb-32 md:pt-40" data-testid="catalog-page">
            <div className="zf-container">
                <Reveal className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <p className="zf-eyebrow">Catálogo</p>
                        <h1 className="mt-4 font-display text-4xl font-black uppercase leading-tight text-forge-paper md:text-6xl">
                            Peças que saíram
                            <br />
                            da <span className="text-forge-yellow">forja</span>
                        </h1>
                        <p className="mt-4 max-w-lg text-forge-dim">
                            Cada item abaixo foi impresso, acabado e inspecionado à mão. Clique na peça para ver os
                            detalhes e comprar.
                        </p>
                    </div>
                    {isAdmin && (
                        <button
                            onClick={() => setAdminOpen(true)}
                            data-testid="admin-open-button"
                            className="inline-flex items-center gap-2 border border-forge-line px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] text-forge-yellow transition-colors duration-300 hover:bg-forge-yellow hover:text-forge-ink"
                        >
                            <Lock className="h-4 w-4" />
                            Área do criador
                        </button>
                    )}
                </Reveal>

                <Reveal delay={0.1} className="mt-14">
                    {isLoading ? (
                        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                            {[0, 1, 2].map((i) => (
                                <div key={i} className="aspect-square animate-pulse bg-forge-panel" />
                            ))}
                        </div>
                    ) : products.length === 0 ? (
                        <div
                            className="layer-lines flex min-h-[360px] flex-col items-center justify-center gap-4 border border-dashed border-forge-line bg-forge-panel/40 p-10 text-center"
                            data-testid="catalog-empty-state"
                        >
                            <PackageOpen className="h-10 w-10 text-forge-yellow/60" />
                            <p className="font-display text-lg font-bold uppercase text-forge-paper">
                                A forja está aquecendo
                            </p>
                            <p className="max-w-sm text-sm leading-relaxed text-forge-dim">
                                Nenhuma peça publicada ainda. Use a área do criador para enviar as fotos das suas
                                primeiras peças.
                            </p>
                        </div>
                    ) : (
                        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                            {products.map((p) => (
                                <article
                                    key={p.id}
                                    data-testid={`product-card-${p.id}`}
                                    className="group relative aspect-square cursor-pointer overflow-hidden border border-forge-line bg-forge-panel transition-colors duration-300 hover:border-forge-yellow/70"
                                >
                                    <img
                                        src={fileUrl(galleryOf(p)[0])}
                                        alt={p.name}
                                        loading="lazy"
                                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent opacity-80 transition-opacity duration-300 group-hover:opacity-100" />
                                    <button
                                        onClick={() => setSelected(p)}
                                        data-testid={`product-details-${p.id}`}
                                        aria-label={`Ver detalhes de ${p.name}`}
                                        className="absolute inset-0 z-10"
                                    />
                                    <FavoriteButton product={p} testid={`product-favorite-${p.id}`} />
                                    {isAdmin && (
                                        <button
                                            onClick={() => setEditProduct(p)}
                                            data-testid={`product-edit-${p.id}`}
                                            aria-label={`Editar ${p.name}`}
                                            className="absolute right-14 top-4 z-20 flex h-9 w-9 items-center justify-center border border-white/20 bg-forge-ink/80 text-forge-paper opacity-0 backdrop-blur-sm transition-all duration-300 hover:border-forge-yellow hover:text-forge-yellow group-hover:opacity-100"
                                        >
                                            <Pencil className="h-4 w-4" />
                                        </button>
                                    )}
                                    {isAdmin && (
                                        <button
                                            onClick={() => onDelete(p.id)}
                                            data-testid={`product-delete-${p.id}`}
                                            aria-label={`Remover ${p.name}`}
                                            className="absolute right-4 top-4 z-20 flex h-9 w-9 items-center justify-center border border-white/20 bg-forge-ink/80 text-forge-paper opacity-0 backdrop-blur-sm transition-all duration-300 hover:border-red-500 hover:text-red-400 group-hover:opacity-100"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    )}
                                    <div className="pointer-events-none absolute inset-x-0 bottom-0 translate-y-2 p-6 pr-20 transition-transform duration-300 group-hover:translate-y-0">
                                        {p.category && (
                                            <span className="mb-1 inline-block bg-forge-ink/80 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-forge-yellow">
                                                {p.category}
                                            </span>
                                        )}
                                        <h3 className="font-display text-lg font-bold uppercase text-forge-paper md:text-xl">
                                            {p.name}
                                        </h3>
                                        {p.price_cents > 0 && (
                                            <p className="mt-1 font-mono text-xs text-forge-yellow" data-testid={`product-price-${p.id}`}>
                                                {formatBRL(p.price_cents)}
                                            </p>
                                        )}
                                        {p.description && (
                                            <p className="mt-1 line-clamp-2 text-sm text-forge-dim">{previewText(p.description)}</p>
                                        )}
                                    </div>
                                    {p.price_cents > 0 && (
                                        <button
                                            onClick={() => {
                                                add(p);
                                                toast.success(`${p.name} foi para o carrinho.`);
                                            }}
                                            data-testid={`product-add-cart-${p.id}`}
                                            aria-label={`Adicionar ${p.name} ao carrinho`}
                                            className="absolute bottom-5 right-5 z-20 flex h-11 w-11 items-center justify-center bg-forge-yellow text-forge-ink transition-all duration-300 hover:scale-110 md:opacity-0 md:group-hover:opacity-100"
                                        >
                                            <Plus className="h-4 w-4" />
                                        </button>
                                    )}
                                </article>
                            ))}
                        </div>
                    )}
                </Reveal>
            </div>

            <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
                <DialogContent
                    className="max-h-[90vh] max-w-3xl overflow-y-auto border-forge-line bg-forge-panel p-0 text-forge-paper"
                    aria-describedby={undefined}
                    data-lenis-prevent="true"
                    data-testid="product-detail-modal"
                >
                    <DialogTitle className="sr-only">{selected?.name || "Detalhes da peça"}</DialogTitle>
                    {selected && (
                        <div className="grid md:grid-cols-2">
                            <div className="flex flex-col">
                                <img
                                    src={fileUrl(galleryOf(selected)[imgIndex] || galleryOf(selected)[0])}
                                    alt={selected.name}
                                    data-testid="product-detail-image"
                                    className="h-64 w-full object-cover md:h-96"
                                />
                                {galleryOf(selected).length > 1 && (
                                    <div className="flex gap-2 overflow-x-auto bg-forge-ink p-2" data-testid="product-gallery-thumbs">
                                        {galleryOf(selected).map((path, i) => (
                                            <button
                                                key={`${path}-${i}`}
                                                onClick={() => setImgIndex(i)}
                                                data-testid={`product-gallery-thumb-${i}`}
                                                aria-label={`Foto ${i + 1} de ${selected.name}`}
                                                className={`h-14 w-14 shrink-0 overflow-hidden border-2 transition-colors duration-300 ${
                                                    i === imgIndex
                                                        ? "border-forge-yellow"
                                                        : "border-transparent opacity-70 hover:opacity-100"
                                                }`}
                                            >
                                                <img src={fileUrl(path)} alt="" className="h-full w-full object-cover" loading="lazy" />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-col p-6 md:p-8">
                                {selected.category && (
                                    <span className="inline-block w-fit bg-forge-ink/80 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-forge-yellow">
                                        {selected.category}
                                    </span>
                                )}
                                <h2 className="mt-2 font-display text-2xl font-bold uppercase leading-tight" data-testid="product-detail-name">
                                    {selected.name}
                                </h2>
                                {selected.price_cents > 0 && (
                                    <p className="mt-2 font-display text-xl font-black text-forge-yellow" data-testid="product-detail-price">
                                        {formatBRL(selected.price_cents)}
                                    </p>
                                )}
                                {selected.description && (
                                    <div
                                        className="mt-4 text-sm leading-relaxed text-forge-dim [&_b]:text-forge-yellow [&_div]:my-1 [&_h3]:font-display [&_h3]:text-forge-paper [&_h4]:font-display [&_h4]:text-forge-paper [&_li]:ml-4 [&_ol]:list-decimal [&_p]:my-1 [&_strong]:text-forge-yellow [&_ul]:list-disc"
                                        data-testid="product-detail-description"
                                        dangerouslySetInnerHTML={{ __html: selected.description }}
                                    />
                                )}
                                <div className="mt-auto space-y-3 pt-8">
                                    <a
                                        href={`https://wa.me/${WHATS_NUMBER}?text=${encodeURIComponent(
                                            `Olá! Vi "${selected.name}" no catálogo da Ziggs Forge e quero comprar.`,
                                        )}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid="product-detail-whatsapp"
                                        className="flex w-full items-center justify-center gap-3 bg-forge-yellow py-4 font-mono text-xs uppercase tracking-[0.2em] text-forge-ink transition-transform duration-300 hover:-translate-y-0.5"
                                    >
                                        <MessageCircle className="h-4 w-4" />
                                        Comprar pelo WhatsApp
                                    </a>
                                    <button
                                        onClick={() => {
                                            add(selected);
                                            toast.success(`${selected.name} foi para o carrinho.`);
                                            setSelected(null);
                                        }}
                                        data-testid="product-detail-add-cart"
                                        className="w-full border border-forge-line py-4 font-mono text-xs uppercase tracking-[0.2em] text-forge-paper transition-colors duration-300 hover:border-forge-yellow hover:text-forge-yellow"
                                    >
                                        Adicionar ao carrinho
                                    </button>
                                    <p className="text-center font-mono text-[10px] uppercase tracking-[0.15em] text-forge-dim">
                                        Prefere pagar pelo site? Adicione ao carrinho e finalize com segurança.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <AdminPanel open={adminOpen} onOpenChange={setAdminOpen} onAuthed={() => setIsAdmin(true)} />

            <ProductEditDialog
                product={editProduct}
                open={!!editProduct}
                onOpenChange={(o) => {
                    if (!o) setEditProduct(null);
                }}
                onSaved={() => qc.invalidateQueries({ queryKey: ["products"] })}
            />
        </section>
    );
};

export default Catalog;
